import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { ArticleCard } from "../components/ArticleCard";
import { Logo } from "../components/Logo";
import { fetchArticle, fetchArticles, CATEGORY_LABELS } from "../lib/api";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    setNotFound(false);
    fetchArticle(slug)
      .then((data) => {
        setArticle(data);
        fetchArticles({ category: data.category }).then((list) =>
          setRelated(list.filter((a) => a.slug !== data.slug).slice(0, 3))
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-ink flex flex-col items-center justify-center gap-6">
        <h1 className="font-serif text-4xl text-white">Story not found</h1>
        <Link to="/" data-testid="back-home-link" className="font-body text-crimson uppercase tracking-widest text-sm">
          Back to Phusic
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <span className="font-logo text-4xl text-white animate-pulse">Phusic</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink grain">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[80vh] w-full overflow-hidden">
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
          src={article.hero_image}
          alt={article.title}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 px-6 lg:px-10 pb-14">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Link
                to={`/category/${article.category}`}
                data-testid="article-category-tag"
                className="inline-block font-body text-xs uppercase tracking-[0.28em] text-crimson mb-5 hover:text-white transition-colors"
              >
                {CATEGORY_LABELS[article.category]} · {article.kicker}
              </Link>
              <h1
                data-testid="article-title"
                className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.08] text-white text-shadow-hero"
              >
                {article.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-3 font-body text-sm text-white/60">
                <span className="text-white/90">{article.author}</span>
                <span className="h-1 w-1 rounded-full bg-white/40" />
                <span>{article.date}</span>
                <span className="h-1 w-1 rounded-full bg-white/40" />
                <span>{article.read_time}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="px-6 lg:px-10 py-12 sm:py-16">
        <div className="mx-auto max-w-2xl">
          <p className="font-serif text-xl sm:text-3xl leading-snug text-white/90 mb-8 sm:mb-10 italic">
            {article.subtitle}
          </p>
          <div data-testid="article-body" className="space-y-6 sm:space-y-7">
            {article.body.map((para, i) => (
              <p key={i} className="font-body text-base sm:text-lg leading-relaxed text-white/75">
                {para}
              </p>
            ))}
          </div>

          {article.gallery?.length > 0 && (
            <div className="mt-14 space-y-6">
              {article.gallery.map((src, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="overflow-hidden rounded-lg border border-white/10"
                >
                  <img
                    src={src}
                    alt={`${article.title} ${i + 1}`}
                    loading="lazy"
                    className="w-full object-cover"
                  />
                </motion.figure>
              ))}
            </div>
          )}
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-white/10 px-6 lg:px-10 py-14 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <h2 className="font-serif text-2xl sm:text-3xl text-white mb-8 sm:mb-10">More in {CATEGORY_LABELS[article.category]}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a, i) => (
                <ArticleCard key={a.slug} article={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-white/10 px-6 lg:px-10 py-10 flex items-center justify-between">
        <Logo size="text-3xl" />
        <Link
          to="/"
          data-testid="footer-home-link"
          className="group inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
          Back to front page
        </Link>
      </footer>
    </div>
  );
}
